import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Text,
  VStack,
  Button,
  useDisclosure,
  useToast,
  Card,
  CardHeader,
  CardBody,
  HStack,
  IconButton,
  Divider,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { useParams } from 'react-router-dom';
import {
  getOrgAIInstructions,
  deleteOrgAIInstruction,
} from '../../hooks/ai_instructions';
import { getOrganization } from '../../hooks/org_data';
import InstructionModal from './InstructionModal';
import {
  Organization,
  OrganizationAICustomInstruction,
} from '../../utils/types';

export default function OrganizationAIDataPage() {
  const { orgId } = useParams();
  const toast = useToast();
  const [organization, setOrganization] = useState<Organization>();
  const [instructions, setInstructions] = useState<
    OrganizationAICustomInstruction[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] =
    useState<OrganizationAICustomInstruction | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue('white', 'gray.800');

  const fetchData = async () => {
    setLoading(true);
    try {
      const orgRes = await getOrganization(orgId!);
      const aiRes = await getOrgAIInstructions(orgId!);
      console.log('orgRes', orgRes);
      if (orgRes.success) {
        setOrganization(orgRes.data);
      }
      if (aiRes.success) {
        setInstructions(aiRes.data);
      }
    } catch (error) {
      toast({ title: 'Failed to load data', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this instruction?')) return;
    try {
      const res = await deleteOrgAIInstruction(orgId!, id);
      if (res.success) {
        toast({ title: 'Instruction deleted', status: 'success' });
        fetchData();
      }
    } catch {
      toast({ title: 'Failed to delete', status: 'error' });
    }
  };

  if (loading) return <Spinner mt={10} />;

  return (
    // <Box p={{ base: 4, md: 6 }}>
    //   <Heading fontSize={"xl"} mb={4}>{organization?.name} — AI Data</Heading>
    //   <Card bg={bg} shadow="md">
    //     <CardHeader>
    //       <HStack justify="space-between">
    //         <Text fontWeight="bold">AI Custom Instructions</Text>
    //         <Button
    //           leftIcon={<AddIcon />}
    //           size="sm"
    //           colorScheme="green"
    //           bg={"#006400"}
    //           onClick={() => { setSelected(null); onOpen(); }}
    //         >
    //           Add Instruction
    //         </Button>
    //       </HStack>
    //     </CardHeader>
    //     {/* <Divider /> */}
    //     <CardBody>
    //       {instructions.length === 0 ? (
    //         <Text color="gray.500">No AI instructions yet.</Text>
    //       ) : (
    //         <VStack align="stretch" spacing={4}>
    //           {instructions && instructions.length > 0 && instructions?.map((ins) => (
    //             <Box
    //               key={ins.id}
    //               p={4}
    //               borderWidth="1px"
    //               borderRadius="md"
    //               _hover={{ shadow: "sm" }}
    //             >
    //               <HStack justify="space-between" align="start">
    //                 <Box>
    //                   <Text fontWeight="semibold" color="green.600">
    //                     {ins.context_type.replace("_", " ").toUpperCase()}
    //                   </Text>
    //                   <Text fontSize="sm" color="gray.600" mt={1}>
    //                     {ins.instruction_text}
    //                   </Text>
    //                 </Box>
    //                 <HStack>
    //                   <IconButton
    //                     icon={<EditIcon />}
    //                     aria-label="Edit"
    //                     size="sm"
    //                     onClick={() => { setSelected(ins); onOpen(); }}
    //                   />
    //                   <IconButton
    //                     icon={<DeleteIcon />}
    //                     aria-label="Delete"
    //                     size="sm"
    //                     colorScheme="red"
    //                     onClick={() => handleDelete(ins.id)}
    //                   />
    //                 </HStack>
    //               </HStack>
    //             </Box>
    //           ))}
    //         </VStack>
    //       )}
    //     </CardBody>
    //   </Card>

    //   <InstructionModal
    //     isOpen={isOpen}
    //     onClose={onClose}
    //     orgId={orgId!}
    //     existing={selected}
    //     onSaved={fetchData}
    //   />
    // </Box>
    <Box p={{ base: 4, md: 6 }}>
      {/* Page Header */}
      <Heading fontSize={{ base: 'xl', md: '2xl' }} mb={6} color="green.700">
        {organization?.name} - AI Data
      </Heading>

      {/* Instructions Card */}
      <Card bg={'white'} shadow="lg" borderRadius="2xl" p={4}>
        <CardHeader px={0} mb={3}>
          <HStack justify="space-between">
            <Text fontWeight="bold" fontSize="md" color="black">
              AI Custom Instructions
            </Text>
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              colorScheme="green"
              bgGradient="linear(to-r, #006400, green.500)"
              _hover={{ bgGradient: 'linear(to-r, #006400, green.400)' }}
              onClick={() => {
                setSelected(null);
                onOpen();
              }}
            >
              Add Instruction
            </Button>
          </HStack>
        </CardHeader>

        <CardBody px={0}>
          {instructions.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={6}>
              No AI instructions yet.
            </Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {instructions.map((ins) => (
                <Box
                  key={ins.id}
                  p={4}
                  bg={'gray.50'}
                  borderRadius="xl"
                  borderWidth={1}
                  borderColor={'gray.200'}
                  _hover={{
                    shadow: 'md',
                    transform: 'scale(1.01)',
                    transition: 'all 0.2s',
                  }}
                >
                  <HStack justify="space-between" align="start">
                    <Box>
                      <Text
                        fontWeight="semibold"
                        color="#006400"
                        fontSize="md"
                        textTransform="uppercase"
                      >
                        {ins.context_type.replace('_', ' ')}
                      </Text>
                      <Text fontSize="sm" color={'gray.700'} mt={1}>
                        {ins.instruction_text}
                      </Text>
                      <Badge
                        mt={2}
                        borderRadius={1}
                        colorScheme={ins.active ? 'green' : 'red'}
                      >
                        {ins.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Box>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<EditIcon />}
                        aria-label="Edit"
                        size="sm"
                        variant="solid"
                        colorScheme="green"
                        bg={'#006400'}
                        onClick={() => {
                          setSelected(ins);
                          onOpen();
                        }}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        aria-label="Delete"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(ins.id)}
                      />
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Modal for Adding/Editing Instructions */}
      <InstructionModal
        isOpen={isOpen}
        onClose={onClose}
        orgId={orgId || ''}
        existing={selected}
        onSaved={fetchData}
      />
    </Box>
  );
}
